import type { SessionSummary } from '@/app/app/research-assistant/types';

export interface ThreadFolder {
  id: string;
  name: string;
  icon: string;
  collapsed: boolean;
  createdAt: string;
}

export interface ThreadOrganizationState {
  pinnedThreadIds: string[];
  folders: ThreadFolder[];
  threadFolderIds: Record<string, string>;
}

export interface OrganizedThreadFolder extends ThreadFolder {
  threads: SessionSummary[];
}

export interface ThreadOrganizationView {
  pinned: SessionSummary[];
  folders: OrganizedThreadFolder[];
  loose: SessionSummary[];
}

interface CreateThreadFolderInput {
  id: string;
  name: string;
  icon?: string;
  createdAt?: string;
}

export const THREAD_FOLDER_EMOJI_OPTIONS = [
  '📁',
  '⭐',
  '🚀',
  '💡',
  '🎯',
  '📌',
  '📊',
  '🔎',
  '🧪',
  '✅',
  '🔥',
  '🌱',
] as const;

export const DEFAULT_THREAD_FOLDER_ICON = THREAD_FOLDER_EMOJI_OPTIONS[0];

export function createThreadOrganizationState(
  input?: Partial<ThreadOrganizationState> | null
): ThreadOrganizationState {
  const pinnedThreadIds = Array.isArray(input?.pinnedThreadIds)
    ? uniqueStrings(input.pinnedThreadIds)
    : [];
  const folders = Array.isArray(input?.folders)
    ? input.folders
        .map(folder => normalizeFolder(folder))
        .filter((folder): folder is ThreadFolder => Boolean(folder))
    : [];
  const folderIds = new Set(folders.map(folder => folder.id));
  const threadFolderIds: Record<string, string> = {};

  if (input?.threadFolderIds && typeof input.threadFolderIds === 'object') {
    for (const [threadId, folderId] of Object.entries(input.threadFolderIds)) {
      if (typeof threadId !== 'string' || typeof folderId !== 'string') continue;
      if (!threadId.trim() || !folderIds.has(folderId)) continue;
      threadFolderIds[threadId] = folderId;
    }
  }

  return {
    pinnedThreadIds,
    folders: uniqueFolders(folders),
    threadFolderIds,
  };
}

export function createThreadFolder(
  state: ThreadOrganizationState,
  folder: CreateThreadFolderInput
): ThreadOrganizationState {
  const normalizedState = createThreadOrganizationState(state);
  const id = folder.id.trim();
  const name = folder.name.trim();
  if (!id || !name || normalizedState.folders.some(existing => existing.id === id)) {
    return normalizedState;
  }

  return {
    ...normalizedState,
    folders: [
      ...normalizedState.folders,
      {
        id,
        name,
        icon: normalizeFolderIcon(folder.icon),
        collapsed: false,
        createdAt: folder.createdAt || new Date().toISOString(),
      },
    ],
  };
}

export function togglePinnedThread(
  state: ThreadOrganizationState,
  threadId: string
): ThreadOrganizationState {
  const normalizedState = createThreadOrganizationState(state);
  const normalizedThreadId = threadId.trim();
  if (!normalizedThreadId) return normalizedState;

  const isPinned = normalizedState.pinnedThreadIds.includes(normalizedThreadId);
  return {
    ...normalizedState,
    pinnedThreadIds: isPinned
      ? normalizedState.pinnedThreadIds.filter(id => id !== normalizedThreadId)
      : [...normalizedState.pinnedThreadIds, normalizedThreadId],
  };
}

export function moveThreadToFolder(
  state: ThreadOrganizationState,
  threadId: string,
  folderId: string | null
): ThreadOrganizationState {
  const normalizedState = createThreadOrganizationState(state);
  const normalizedThreadId = threadId.trim();
  if (!normalizedThreadId) return normalizedState;

  const nextThreadFolderIds = { ...normalizedState.threadFolderIds };
  if (!folderId) {
    delete nextThreadFolderIds[normalizedThreadId];
    return {
      ...normalizedState,
      threadFolderIds: nextThreadFolderIds,
    };
  }

  const normalizedFolderId = folderId.trim();
  const folderExists = normalizedState.folders.some(folder => folder.id === normalizedFolderId);
  if (!folderExists) return normalizedState;

  nextThreadFolderIds[normalizedThreadId] = normalizedFolderId;
  return {
    ...normalizedState,
    threadFolderIds: nextThreadFolderIds,
  };
}

export function setThreadFolderCollapsed(
  state: ThreadOrganizationState,
  folderId: string,
  collapsed: boolean
): ThreadOrganizationState {
  const normalizedState = createThreadOrganizationState(state);
  const normalizedFolderId = folderId.trim();
  if (!normalizedFolderId) return normalizedState;

  return {
    ...normalizedState,
    folders: normalizedState.folders.map(folder =>
      folder.id === normalizedFolderId ? { ...folder, collapsed } : folder
    ),
  };
}

export function setThreadFolderIcon(
  state: ThreadOrganizationState,
  folderId: string,
  icon: string
): ThreadOrganizationState {
  const normalizedState = createThreadOrganizationState(state);
  const normalizedFolderId = folderId.trim();
  if (!normalizedFolderId) return normalizedState;

  return {
    ...normalizedState,
    folders: normalizedState.folders.map(folder =>
      folder.id === normalizedFolderId ? { ...folder, icon: normalizeFolderIcon(icon) } : folder
    ),
  };
}

export function buildThreadOrganizationView(
  sessions: SessionSummary[],
  state: ThreadOrganizationState
): ThreadOrganizationView {
  const normalizedState = createThreadOrganizationState(state);
  const sessionsById = new Map(sessions.map(session => [session.id, session]));
  const pinnedIds = new Set(
    normalizedState.pinnedThreadIds.filter(threadId => sessionsById.has(threadId))
  );
  const folderIds = new Set(normalizedState.folders.map(folder => folder.id));
  const folderThreads = new Map<string, SessionSummary[]>(
    normalizedState.folders.map(folder => [folder.id, []])
  );
  const loose: SessionSummary[] = [];

  for (const session of sessions) {
    if (pinnedIds.has(session.id)) continue;
    const folderId = normalizedState.threadFolderIds[session.id];
    if (folderId && folderIds.has(folderId)) {
      folderThreads.get(folderId)?.push(session);
    } else {
      loose.push(session);
    }
  }

  return {
    pinned: sessions.filter(session => pinnedIds.has(session.id)),
    folders: normalizedState.folders.map(folder => ({
      ...folder,
      threads: folderThreads.get(folder.id) || [],
    })),
    loose,
  };
}

export function mergeThreadOrganizationSessions(
  recentSessions: SessionSummary[],
  organizationSessions: SessionSummary[]
): SessionSummary[] {
  const seen = new Set<string>();
  const merged: SessionSummary[] = [];

  for (const session of [...recentSessions, ...organizationSessions]) {
    if (seen.has(session.id)) continue;
    seen.add(session.id);
    merged.push(session);
  }

  return merged;
}

function normalizeFolder(value: unknown): ThreadFolder | null {
  if (!value || typeof value !== 'object') return null;
  const folder = value as Partial<ThreadFolder>;
  const id = typeof folder.id === 'string' ? folder.id.trim() : '';
  const name = typeof folder.name === 'string' ? folder.name.trim() : '';
  if (!id || !name) return null;
  return {
    id,
    name,
    icon: normalizeFolderIcon(folder.icon),
    collapsed: folder.collapsed === true,
    createdAt:
      typeof folder.createdAt === 'string' && folder.createdAt.trim()
        ? folder.createdAt
        : new Date(0).toISOString(),
  };
}

function normalizeFolderIcon(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_THREAD_FOLDER_ICON;
  const normalizedValue = value.trim();
  return THREAD_FOLDER_EMOJI_OPTIONS.some(icon => icon === normalizedValue)
    ? normalizedValue
    : DEFAULT_THREAD_FOLDER_ICON;
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const normalizedValue = value.trim();
    if (!normalizedValue || seen.has(normalizedValue)) continue;
    seen.add(normalizedValue);
    result.push(normalizedValue);
  }
  return result;
}

function uniqueFolders(folders: ThreadFolder[]): ThreadFolder[] {
  const seen = new Set<string>();
  const result: ThreadFolder[] = [];
  for (const folder of folders) {
    if (seen.has(folder.id)) continue;
    seen.add(folder.id);
    result.push(folder);
  }
  return result;
}
